import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { 
  Play, 
  Square, 
  Clock, 
  DollarSign, 
  Edit3, 
  Trash2, 
  Plus,
  Save,
  X,
  Timer,
  BarChart3,
  Calendar
} from 'lucide-react';

const formatDuration = (minutes) => {
  if (!minutes) return '0m';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${mins}m`;
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString();
};

const TimerWidget = ({ currentTimer, onTimerAction }) => {
  const [currentDuration, setCurrentDuration] = useState(0);

  useEffect(() => {
    let interval;
    if (currentTimer?.is_running) {
      interval = setInterval(() => {
        const now = new Date();
        const startTime = new Date(currentTimer.time_entry.start_time);
        const diffMinutes = Math.floor((now - startTime) / (1000 * 60));
        setCurrentDuration(diffMinutes);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentTimer]);

  if (!currentTimer?.is_running) {
    return (
      <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
        <Timer className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-500 text-sm">No timer running</p>
      </div>
    );
  }

  return (
    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <Clock className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <div className="font-semibold text-green-800">
              {formatDuration(currentDuration)}
            </div>
            <div className="text-sm text-green-600">
              {currentTimer.task_name}
            </div>
          </div>
        </div>
        <button
          onClick={() => onTimerAction('stop', currentTimer.time_entry.task_id)}
          className="flex items-center space-x-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Square className="w-4 h-4" />
          <span>Stop</span>
        </button>
      </div>
    </div>
  );
};

const TimeEntryForm = ({ isOpen, onClose, onSave, taskId, entry = null }) => {
  const [formData, setFormData] = useState({
    description: '',
    start_time: '',
    end_time: '',
    is_billable: true,
    hourly_rate: '',
    tags: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry) {
      setFormData({
        description: entry.description || '',
        start_time: entry.start_time ? new Date(entry.start_time).toISOString().slice(0, 16) : '',
        end_time: entry.end_time ? new Date(entry.end_time).toISOString().slice(0, 16) : '',
        is_billable: entry.is_billable,
        hourly_rate: entry.hourly_rate || '',
        tags: entry.tags || []
      });
    } else {
      const now = new Date();
      setFormData({
        description: '',
        start_time: now.toISOString().slice(0, 16),
        end_time: '',
        is_billable: true,
        hourly_rate: '',
        tags: []
      });
    }
  }, [entry, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const submitData = {
        ...formData,
        start_time: formData.start_time ? new Date(formData.start_time).toISOString() : null,
        end_time: formData.end_time ? new Date(formData.end_time).toISOString() : null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        ...(entry ? {} : { task_id: taskId })
      };

      await onSave(submitData, entry?.id);
      onClose();
    } catch (error) {
      console.error('Failed to save time entry:', error);
      alert('Failed to save time entry. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {entry ? 'Edit Time Entry' : 'Add Manual Time Entry'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What did you work on?"
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time
              </label>
              <input
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time
              </label>
              <input
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hourly Rate ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.hourly_rate}
                onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                placeholder="0.00"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_billable"
                  checked={formData.is_billable}
                  onChange={(e) => setFormData({ ...formData, is_billable: e.target.checked })}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="is_billable" className="text-sm font-medium text-gray-700">
                  Billable
                </label>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : entry ? 'Update' : 'Save'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TimeTracking = ({ taskId, projectId, isCompact = false }) => {
  const [currentTimer, setCurrentTimer] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  const loadTimerStatus = useCallback(async () => {
    try {
      const response = await api.get('/timer/status');
      setCurrentTimer(response.data.is_running ? response.data : null);
    } catch (error) {
      console.error('Failed to load timer status:', error);
    }
  }, []);

  const loadTimeEntries = useCallback(async () => {
    if (!taskId && !projectId) return;
    
    try {
      const endpoint = taskId 
        ? `tasks/${taskId}/time-entries`
        : `projects/${projectId}/time-entries`;
      
      const response = await api.get(`/${endpoint}`);
      
      setTimeEntries(response.data || []);
    } catch (error) {
      console.error('Failed to load time entries:', error);
      setTimeEntries([]);
    } finally {
      setLoading(false);
    }
  }, [taskId, projectId]);

  useEffect(() => {
    loadTimerStatus();
    loadTimeEntries();
  }, [loadTimerStatus, loadTimeEntries]);

  const handleTimerAction = async (action, targetTaskId) => {
    try {
      await api.post('/timer', {
        action,
        task_id: targetTaskId,
        description: action === 'start' ? 'Timer started' : null
      });

      // Reload timer status and time entries
      await loadTimerStatus();
      await loadTimeEntries();
    } catch (error) {
      console.error('Timer action failed:', error);
      alert('Timer action failed. Please try again.');
    }
  };

  const handleSaveTimeEntry = async (entryData, entryId = null) => {
    if (entryId) {
      // Update existing entry
      await api.put(`/time-entries/${entryId}`, entryData);
    } else {
      // Create new entry
      await api.post('/time-entries', entryData);
    }
    
    await loadTimeEntries();
    setEditingEntry(null);
    setShowForm(false);
  };

  const handleDeleteEntry = async (entryId) => {
    if (!confirm('Are you sure you want to delete this time entry?')) return;

    try {
      await api.delete(`/time-entries/${entryId}`);
      
      await loadTimeEntries();
    } catch (error) {
      console.error('Failed to delete time entry:', error);
      alert('Failed to delete time entry. Please try again.');
    }
  };

  if (isCompact) {
    return (
      <div className="space-y-3">
        {/* Compact Timer Widget */}
        <TimerWidget currentTimer={currentTimer} onTimerAction={handleTimerAction} />
        
        {/* Start Timer Button (only show if no timer running and we have a taskId) */}
        {taskId && !currentTimer?.is_running && (
          <button
            onClick={() => handleTimerAction('start', taskId)}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Play className="w-4 h-4" />
            <span>Start Timer</span>
          </button>
        )}
        
        {/* Quick Stats */}
        {timeEntries.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm text-gray-600 mb-1">Total Time</div>
            <div className="font-semibold text-gray-900">
              {formatDuration(timeEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Timer Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
          <Timer className="w-5 h-5 text-purple-600" />
          <span>Time Tracking</span>
        </h3>

        <div className="space-y-4">
          <TimerWidget currentTimer={currentTimer} onTimerAction={handleTimerAction} />
          
          {/* Action Buttons */}
          <div className="flex space-x-3">
            {taskId && !currentTimer?.is_running && (
              <button
                onClick={() => handleTimerAction('start', taskId)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Play className="w-4 h-4" />
                <span>Start Timer</span>
              </button>
            )}
            
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Manual Entry</span>
            </button>
          </div>
        </div>
      </div>

      {/* Time Entries List */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            <span>Time Entries</span>
          </h3>
          
          {timeEntries.length > 0 && (
            <div className="text-sm text-gray-500">
              Total: {formatDuration(timeEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="text-gray-500">Loading time entries...</div>
          </div>
        ) : timeEntries.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No time entries yet</p>
            <p className="text-sm text-gray-400 mt-1">Start a timer or add a manual entry to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {timeEntries.map((entry) => (
              <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="font-medium text-gray-900">
                        {formatDuration(entry.duration_minutes)}
                      </div>
                      {entry.is_billable && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          Billable
                        </span>
                      )}
                      {entry.is_running && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          Running
                        </span>
                      )}
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-2">
                      {entry.description}
                    </div>
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatTime(entry.start_time)}</span>
                        {entry.end_time && (
                          <span>→ {formatTime(entry.end_time)}</span>
                        )}
                      </div>
                      
                      {entry.hourly_rate && (
                        <div className="flex items-center space-x-1">
                          <DollarSign className="w-3 h-3" />
                          <span>${entry.hourly_rate}/hr</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => {
                        setEditingEntry(entry);
                        setShowForm(true);
                      }}
                      className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteEntry(entry.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Time Entry Form Modal */}
      <TimeEntryForm
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingEntry(null);
        }}
        onSave={handleSaveTimeEntry}
        taskId={taskId}
        entry={editingEntry}
      />
    </div>
  );
};

export default TimeTracking;